import { create } from 'zustand'
import { ICinematics, IScene } from '@/types'
import { IGlobalProps } from '@/types/cinematics'
import { QueryClient } from '@tanstack/react-query'

import settings from '@/settings/public'

function findSceneByRoute(scenes: Record<string, IScene> | undefined, path: string): IScene | undefined {
  if (!path || !scenes) return undefined
  return Object.values(scenes).find((scene) => scene.route === path)
}

function buildCinematicsState(cinematics: ICinematics, currentPath: string) {
  const { scenes, ambiance, props } = cinematics
  return {
    cinematics,
    scenes,
    ambiance,
    props,
    scene: findSceneByRoute(scenes, currentPath),
  }
}

interface CinematicsState {
  cinematics: ICinematics
  originalCinematics: ICinematics | null
  scene: IScene | undefined
  scenes: { [key: string]: IScene }
  ambiance: ICinematics['ambiance']
  props: IGlobalProps
  appCode: string
  locale: string
  initialLanguage: string
  path: string
  isUsingLocalCinematics: boolean
  appliedABTag?: string
  setPath: (path: string) => void
  setCinematics: (cinematics: ICinematics, queryClient?: QueryClient) => void
  resetCinematics: (cinematics: ICinematics) => void
  applyABCinematics: (cinematics: ICinematics, tag?: string) => void
  initialize: (cinematics: ICinematics, initialLanguage: string, isUsingLocalCinematics?: boolean) => void
}

export const useCinematicsStore = create<CinematicsState>((set, get) => {

  return {
    cinematics: {} as ICinematics,
    originalCinematics: null,
    scene: undefined,
    scenes: {},
    ambiance: {} as ICinematics['ambiance'],
    props: {} as IGlobalProps,
    appCode: settings?.app?.code || '',
    locale: '',
    initialLanguage: '',
    path: '',
    isUsingLocalCinematics: false,

    setPath: (path) => {
      set((state) => ({
        path,
        scene: findSceneByRoute(state.scenes, path),
      }))
    },
    setCinematics: (cinematics) => {
      set({ cinematics })
    },
    resetCinematics: (cinematics) => {
      // Clear the applied AB tag here too, not only in `initialize`. This is the
      // mutator used by both paths that STOP rendering the AB variant — logout
      // revert (useBranding revertToOriginal) and branding-over-AB — and an SPA
      // logout has no page reload, so a surviving tag would mislabel the NEXT
      // user's retention/GTM events (their PII, the previous arm). See PRO-344 L1.
      set({ ...buildCinematicsState(cinematics, get().path), appliedABTag: undefined })
    },
    applyABCinematics: (cinematics, tag) => {
      // appliedABTag is only set here, at the moment the AB variant actually
      // renders — a bucketed-but-never-applied decision (e.g. branding won the
      // race) must not report an exposure it never showed.
      set({
        ...buildCinematicsState(cinematics, get().path),
        originalCinematics: cinematics,
        appliedABTag: tag,
      })
    },
    initialize: (cinematics, initialLanguage, isUsingLocalCinematics = false) => {
      // A re-initialize (e.g. CinematicsRecovery) starts from a fresh base
      // cinematic — any previously applied AB tag no longer describes what
      // renders, so it must not leak into analytics.
      set({
        ...buildCinematicsState(cinematics, get().path),
        originalCinematics: cinematics,
        initialLanguage,
        isUsingLocalCinematics,
        appliedABTag: undefined,
      })
    },
  }
})
